import { type FC, type ReactNode, useContext, useMemo } from "react";
import { FilterContext, type FilterContextValue } from "./filterContext.js";
import type { FilterValues } from "./filterState.js";

export interface FilterDefaultOverridesProviderProps {
  overrides: Partial<FilterValues>;
  children?: ReactNode;
}

/**
 * Layers page-scoped default filter values over the app-level
 * `FilterProvider`, for defaults that depend on page context the global
 * provider can't know (e.g. the current playbook). Overridden defaults behave
 * exactly like built-in ones: they render as removable chips, user changes
 * win over them, and Reset restores them.
 */
export const FilterDefaultOverridesProvider: FC<
  FilterDefaultOverridesProviderProps
> = ({ overrides, children }) => {
  const parent = useContext(FilterContext);

  const contextValue = useMemo<FilterContextValue>(() => {
    const defaultValues = { ...parent.defaultValues, ...overrides };
    return {
      ...parent,
      defaultValues,
      values: { ...defaultValues, ...parent.changedValues },
    };
  }, [parent, overrides]);

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};
